import { GetStaticPaths, GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import { parse as parse5Parse } from 'parse5'
import { parse5ToStructuredText } from 'datocms-html-to-structured-text'
import styled from '@emotion/styled'
import { css } from '@emotion/react'

import { SiteLocale } from '../../graphql/datoSchema.generated'
import stripUndefined from '../../utils/stripUndefined'
import Layout from '../../components/Layout'
import { getSiteConfig, SiteConfig } from '../../graphql/siteConfig'
import { getLayout, Layout as LayoutType } from '../../graphql/layout'
import absolutify from '../../utils/absolutify'
import Seo from '../../components/Seo'

import type { jobResponse } from '../../utils/getVacancyTypes'
import getVacanciesOverview from '../../utils/getVacanciesOverview'
import getVacancyDetails from '../../utils/getVacancyDetails'
import slugify from '../../utils/slugify'
import MediaHeader from '../../sections/MediaHeader'
import TextSection from '../../sections/TextSection'
import RichText from '../../components/RichText'
import { getLocales } from '../../utils/env'
import { getSiteSettings, SiteSettings } from '../../graphql/siteSettings'
import YellowYardForm from '../../components/YellowYardForm'

const StyledTextHeading = styled.h2(({ theme }) => [
  css`
    ${theme.text.heading2(theme)}
    position: relative;
    margin-bottom: ${theme.spacing.x2}px;

    // breakpoint styling is needed to override the h2 styling
    @media screen and (min-width: ${theme.breakpoints.desktop}px) {
      margin-bottom: ${theme.spacing.x2}px;
    }
  `,
])

const StyledRichText = styled(RichText)(({ theme }) => [
  css`
    margin-bottom: ${theme.spacing.x6}px;

    @media screen and (min-width: ${theme.breakpoints.desktop}px) {
      margin-bottom: ${theme.spacing.x9}px;
    }
  `,
])

const formatJobUrl = (id: string, name: string) => {
  const formattedId = id.slice(0, 6)
  const formattedName = slugify(name)

  return `${formattedId}-${formattedName}`
}

const getVacanciesSlugs = async () => {
  return (
    await Promise.all(
      getLocales().map(async (locale) => {
        const siteConfig = await getSiteConfig(locale as SiteLocale)
        // When unset, this site doesn't include a jobs listing
        if (!siteConfig.jobsIndex?.slug) return []

        const vacancyListPages = await getVacanciesOverview()
        const vacancies = vacancyListPages.items.map((page) => page).flat()
        return vacancies.map(({ id, functionName }) => ({
          slug: formatJobUrl(id, functionName),
          locale,
        }))
      }),
    )
  ).flat()
}

export const getStaticPaths: GetStaticPaths = async () => {
  if (process.env.FEATUREFLAG_STATICPATHS !== 'true') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  return {
    paths: (await getVacanciesSlugs()).map(({ slug, locale }) => ({
      params: { slug },
      locale,
    })),
    fallback: 'blocking',
  }
}

export interface StaticProps {
  siteConfig: SiteConfig
  siteSettings: SiteSettings
  layout: LayoutType
  vacancy: jobResponse
}

export const getStaticProps: GetStaticProps<
  StaticProps,
  { slug: string | string[] }
> = async ({ params, locale }) => {
  if (!locale) {
    throw new Error('Next.js is misconfigured: locale is missing')
  }

  const siteConfig = await getSiteConfig(locale as SiteLocale)
  // When unset, this site doesn't include a jobs listing
  if (!siteConfig.jobsIndex?.slug) {
    return {
      notFound: true,
      // TODO: Configure via environment variables
      revalidate: 60,
    }
  }

  const slugPath = Array.isArray(params?.slug)
    ? params?.slug?.join('/').toLowerCase()
    : params?.slug?.toLowerCase()
  const slug = slugPath ?? ''
  if (!slug) {
    throw new Error('Missing required param: slug')
  }

  const vacancyListPages = await getVacanciesOverview()
  const vacancies = vacancyListPages.items.map((page) => page).flat()
  const partialId = slug.split('-')[0]
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const vacancyDetailId = vacancies.find(
    (vacancy) =>
      vacancy.id.startsWith(partialId) &&
      slug.split('-').splice(1).join('-') === slugify(vacancy.functionName),
  )?.id

  if (!vacancyDetailId) {
    return {
      notFound: true,
      // TODO: Configure via environment variables
      revalidate: 60,
    }
  }

  const [layout, vacancy, _site] = await Promise.all([
    getLayout(locale as SiteLocale),
    getVacancyDetails(vacancyDetailId),
    getSiteSettings(locale as SiteLocale),
  ])

  if (!vacancy) {
    return {
      notFound: true,
      // TODO: Configure via environment variables
      revalidate: 60,
    }
  }

  return {
    props: stripUndefined({
      siteConfig,
      siteSettings: _site,
      layout,
      vacancy: {
        ...vacancy,
        general: {
          ...vacancy?.general,
          jobTextLong: await parse5ToStructuredText(
            parse5Parse(vacancy.general.jobTextLong, {
              sourceCodeLocationInfo: true,
            }),
          ),
          jobOffer: await parse5ToStructuredText(
            parse5Parse(vacancy.general.jobOffer, {
              sourceCodeLocationInfo: true,
            }),
          ),
          functionCriteria: await parse5ToStructuredText(
            parse5Parse(vacancy.general.functionCriteria, {
              sourceCodeLocationInfo: true,
            }),
          ),
          companyProfile: await parse5ToStructuredText(
            parse5Parse(vacancy.general.companyProfile, {
              sourceCodeLocationInfo: true,
            }),
          ),
        },
      },
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      path: `/${siteConfig.jobsIndex!.slug}/${slug}`,
    }),
    // TODO: Configure via environment variables
    revalidate: 60,
  }
}

const PageResolver = ({
  siteConfig,
  siteSettings,
  layout,
  vacancy,
}: StaticProps) => {
  const { locale } = useRouter()
  if (!locale) {
    throw new Error('locale is missing')
  }
  const {
    functionName,
    experience,
    jobTextLong,
    companyProfile,
    functionCriteria,
    jobOffer,
    jobReactionURL,
  } = vacancy.general
  const {
    roleTitle,
    jobOfferTitle,
    functionCriteriaTitle,
    companyProfileTitle,
    applyFormTitle,
    applyText,
  } = siteConfig.jobDetail[0]
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const canonicalPath = `/${siteConfig.jobsIndex!.slug}/${formatJobUrl(
    vacancy.id,
    functionName,
  )}`

  const fallbackTitle = siteSettings.globalSeo?.fallbackSeo?.title || undefined
  const fallbackDescription =
    siteSettings.globalSeo?.fallbackSeo?.description || undefined
  const formattedSeoTitle = `${functionName || fallbackTitle} ${
    siteSettings.globalSeo?.titleSuffix
  }`

  return (
    <Layout
      siteConfig={siteConfig}
      layout={layout}
      breadcrumbs={[
        {
          key: 'homepage',
          href: '/',
          title: siteConfig.homepageName || 'Home',
        },
        {
          key: 'vacancies',
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          href: `/${siteConfig.jobsIndex!.slug}/`,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          title: siteConfig.jobsIndex!.seo?.title || 'Jobs',
        },
        {
          key: 'vacancy',
          href: canonicalPath,
          title: functionName || '?',
        },
      ]}
    >
      <Seo
        title={formattedSeoTitle}
        description={functionName || fallbackDescription}
        canonical={absolutify(canonicalPath, locale as SiteLocale)}
        paths={{ nl: '', en: '' }}
      />
      <MediaHeader title={functionName} subline={[`${experience}`]} />
      <TextSection>
        {jobTextLong && (
          <>
            <StyledTextHeading>{roleTitle}</StyledTextHeading>
            <StyledRichText text={jobTextLong} />
          </>
        )}
        {companyProfile && (
          <>
            <StyledTextHeading>{companyProfileTitle}</StyledTextHeading>
            <StyledRichText text={companyProfile} />
          </>
        )}
        {functionCriteria && (
          <>
            <StyledTextHeading>{functionCriteriaTitle}</StyledTextHeading>
            <StyledRichText text={functionCriteria} />
          </>
        )}
        {jobOffer && (
          <>
            <StyledTextHeading>{jobOfferTitle}</StyledTextHeading>
            <StyledRichText text={jobOffer} />
          </>
        )}

        {jobReactionURL && (
          <>
            <StyledTextHeading>{applyFormTitle}</StyledTextHeading>
            <StyledRichText text={applyText} />
            <YellowYardForm url={jobReactionURL} />
          </>
        )}
      </TextSection>
    </Layout>
  )
}

export default PageResolver
